import { computed, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useApi } from '@shopexperts/shared';
import type { AuthCheckResponse, AuthCheckClientResponse } from '@shopexperts/shared';
import { clearStoredConvexTokenCache } from '@/app/composables/useConvexAuthToken';
import { useAuthStore } from '@/store/auth';

/**
 * Composable for checking the current user's authentication status.
 * 
 * Uses the auth check endpoint to verify the user is logged in
 * and determine if they are a client (vs an expert).
 * 
 * @example
 * ```ts
 * const { isAuthenticated, isClient, user, client, isLoading } = useAuthCheck();
 * 
 * if (isAuthenticated.value && isClient.value) {
 *   console.log('Logged in as client:', client.value?.company_name);
 * }
 * ```
 */
export function useAuthCheck() {
  const api = useApi();
  const authStore = useAuthStore();

  // Only run the query if we have a token
  const hasToken = computed(() => !!authStore.token);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['auth', 'check'],
    queryFn: async (): Promise<AuthCheckResponse> => {
      return await api.public.auth.check();
    },
    enabled: hasToken,
    staleTime: 60000, // 1 minute
    retry: false, // Don't retry on auth failures
  });

  /**
   * Whether the user is authenticated (has valid token and passed auth check)
   */
  const isAuthenticated = computed(() => {
    return hasToken.value && !!data.value && !isError.value;
  });

  /**
   * Whether the authenticated user is a client
   */
  const isClient = computed(() => {
    return isAuthenticated.value && data.value?.type === 'client';
  });

  /**
   * Whether the authenticated user is an expert
   */
  const isExpert = computed(() => {
    return isAuthenticated.value && data.value?.type === 'expert';
  });

  /**
   * The authenticated user's basic info
   */
  const user = computed(() => data.value?.user);

  /**
   * The user type: 'client' or 'expert'
   */
  const userType = computed(() => data.value?.type);

  /**
   * Whether the user's email is validated
   */
  const isValidated = computed(() => data.value?.is_validated ?? false);

  /**
   * Client-specific data (only available if user is a client)
   */
  const client = computed(() => {
    if (data.value?.type === 'client') {
      return (data.value as AuthCheckClientResponse).client;
    }
    return undefined;
  });

  watch(isError, (nextIsError) => {
    if (nextIsError) {
      clearStoredConvexTokenCache();
    }
  });

  /**
   * Organization data (only available if user is a client)
   */
  const organization = computed(() => {
    if (data.value?.type === 'client') {
      return (data.value as AuthCheckClientResponse).organization;
    }
    return undefined;
  });

  return {
    // Auth status
    isAuthenticated,
    isClient,
    isExpert,
    isValidated,
    
    // User data
    user,
    userType,
    client,
    organization,
    
    // Query state
    data,
    isLoading,
    isError,
    error,
    refetch,
  };
}

